import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  HelpCircle,
  Search,
  Zap,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowLeftRight,
  Package,
  Store,
  Building2,
  Users,
  Shield,
  Truck,
  Tags,
  Ruler,
  BarChart3,
  Settings,
  LayoutDashboard,
  ChevronRight,
  CheckCircle2,
  Lightbulb,
  BookOpen,
  Star
} from 'lucide-react';
import { cn } from '../lib/utils';

const guideCategories = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    icon: Lightbulb,
    color: 'text-amber-600 bg-amber-100',
    guides: [
      {
        title: 'First Time Setup',
        steps: [
          'Login with your credentials provided by the administrator',
          'Click "Initialize Data" on the Dashboard to set up default data (first time only)',
          'Explore the sidebar menu to see available modules',
          'Start with Quick Actions for fast stock operations'
        ]
      },
      {
        title: 'Understanding the Dashboard',
        steps: [
          'View key metrics: Properties, Stores, Items, Stock levels',
          'Monitor low stock alerts in the alerts section',
          'Check recent transactions for latest activity',
          'Use filters to view data for specific properties or stores'
        ]
      }
    ]
  },
  {
    id: 'quick-actions',
    title: 'Quick Actions (Fast Entry)',
    icon: Zap,
    color: 'text-purple-600 bg-purple-100',
    guides: [
      {
        title: 'Quick Stock Issue',
        subtitle: 'Issue items from store in under 10 seconds',
        steps: [
          'Go to Quick Issue from sidebar',
          'Step 1: Select the store you\'re issuing from',
          'Step 2: Search for the item OR pick from Favorites/Recent',
          'Step 3: Enter quantity and select department (Kitchen, Housekeeping, etc.)',
          'Step 4: Review and confirm'
        ],
        tips: [
          'Star frequently used items to add to Favorites',
          'Last used store is remembered automatically',
          'Use quick quantity buttons (1, 5, 10, 25) for speed'
        ]
      },
      {
        title: 'Quick Stock Inward',
        subtitle: 'Receive stock quickly',
        steps: [
          'Go to Quick Inward from sidebar',
          'Step 1: Select the receiving store',
          'Step 2: Search and add items with quantity and rate',
          'Step 3: Select vendor and enter invoice number (optional)',
          'Step 4: Review totals and confirm'
        ],
        tips: [
          'Add multiple items before confirming',
          'Running total is calculated automatically',
          'Vendor selection is optional for direct purchases'
        ]
      },
      {
        title: 'Quick Stock Transfer',
        subtitle: 'Move stock between stores',
        steps: [
          'Go to Quick Transfer from sidebar',
          'Step 1: Select source store (From)',
          'Step 2: Select destination store (To)',
          'Step 3: Search items and enter transfer quantity',
          'Step 4: Review and confirm transfer'
        ],
        tips: [
          'Only items with available stock can be transferred',
          'System prevents transferring more than available',
          'Available quantity shown for each item'
        ]
      }
    ]
  },
  {
    id: 'stock-operations',
    title: 'Stock Operations',
    icon: Package,
    color: 'text-green-600 bg-green-100',
    guides: [
      {
        title: 'Stock Inward (Detailed)',
        subtitle: 'For detailed receiving with full options',
        steps: [
          'Go to Stock Inward from sidebar',
          'Click "New Inward" button',
          'Select Property, Store, and Vendor',
          'Add items with quantity, unit, and rate',
          'Enter invoice number and remarks',
          'Click "Create Inward" to save'
        ]
      },
      {
        title: 'Stock Issue (Detailed)',
        subtitle: 'For detailed issue with full options',
        steps: [
          'Go to Stock Issue from sidebar',
          'Click "New Issue" button',
          'Select Property, Store, and Issue To department',
          'Add items with quantity',
          'System validates against available stock',
          'Click "Create Issue" to save'
        ]
      },
      {
        title: 'Stock Transfer (Detailed)',
        subtitle: 'For detailed transfers with remarks',
        steps: [
          'Go to Stock Transfer from sidebar',
          'Click "New Transfer" button',
          'Select Property, From Store, and To Store',
          'Add items with transfer quantity',
          'Add remarks if needed',
          'Click "Create Transfer" to save'
        ]
      },
      {
        title: 'View Current Stock',
        steps: [
          'Go to Current Stock from sidebar',
          'View all items with their stock quantities',
          'Use filters to view by store or category',
          'Export to CSV for reporting'
        ]
      }
    ]
  },
  {
    id: 'masters',
    title: 'Master Data Setup',
    icon: Building2,
    color: 'text-blue-600 bg-blue-100',
    guides: [
      {
        title: 'Managing Properties',
        steps: [
          'Go to Properties from sidebar',
          'Click "Add Property" to create new',
          'Enter name, code, address, and contact details',
          'Set status to Active/Inactive',
          'Edit or delete using action buttons'
        ]
      },
      {
        title: 'Managing Stores',
        steps: [
          'Go to Stores from sidebar',
          'Click "Add Store" to create new',
          'Select the property this store belongs to',
          'Enter store name, code, and type',
          'Each property can have multiple stores'
        ]
      },
      {
        title: 'Managing Items',
        steps: [
          'Go to Items from sidebar',
          'Click "Add Item" to create new',
          'Enter item name and SKU code',
          'Select category, unit, and vendor',
          'Set minimum stock level for alerts',
          'Upload an image or paste image URL (optional)',
          'Set purchase date and expiry date if applicable',
          'Upload purchase bill/invoice (PDF, Word, Excel, or image)',
          'Enter bill number for reference',
          'Items are shared across all stores'
        ],
        tips: [
          'Upload images up to 5MB in JPG, PNG, GIF, or WebP format',
          'Bills can be uploaded as PDF, Word, Excel, or images (up to 10MB)',
          'Expiry dates help track perishable items',
          'Items with expired dates are highlighted in red',
          'Purchase date and bill help with audit trails'
        ]
      },
      {
        title: 'Managing Categories',
        steps: [
          'Go to Categories from sidebar',
          'Default categories are pre-loaded',
          'Add new categories as needed',
          'Categories help organize items'
        ]
      },
      {
        title: 'Managing Units',
        steps: [
          'Go to Units from sidebar',
          '60+ hospitality units are pre-loaded',
          'Add custom units if needed',
          'Units define how items are measured'
        ]
      },
      {
        title: 'Managing Vendors',
        steps: [
          'Go to Vendors from sidebar',
          'Click "Add Vendor" to create new',
          'Enter vendor name, contact, and address',
          'Link vendors to items for easy tracking'
        ]
      }
    ]
  },
  {
    id: 'reports',
    title: 'Reports',
    icon: BarChart3,
    color: 'text-indigo-600 bg-indigo-100',
    guides: [
      {
        title: 'Viewing Reports',
        steps: [
          'Go to Reports from sidebar',
          'Select report type from tabs',
          'Use filters to narrow down data',
          'Click "Export CSV" to download data'
        ]
      },
      {
        title: 'Available Reports',
        steps: [
          'Current Stock: View stock levels across stores',
          'Stock Inward: View all receiving transactions',
          'Stock Issue: View all issue transactions',
          'Stock Transfer: View all transfer transactions',
          'Low Stock: View items below minimum level'
        ]
      }
    ]
  },
  {
    id: 'administration',
    title: 'Administration',
    icon: Shield,
    color: 'text-red-600 bg-red-100',
    guides: [
      {
        title: 'Managing Users',
        steps: [
          'Go to Users from sidebar',
          'Click "Add User" to create new',
          'Enter name, email, and password',
          'Assign a role to the user',
          'Optionally assign specific stores'
        ]
      },
      {
        title: 'Managing Roles',
        steps: [
          'Go to Roles from sidebar',
          'Default roles: Super Admin, Admin, Property Manager, Store Manager, Staff',
          'Each role has different access levels',
          'Create custom roles as needed'
        ]
      },
      {
        title: 'App Settings (Super Admin Only)',
        steps: [
          'Go to App Settings from sidebar',
          'Change login page banner image',
          'Update app name and tagline',
          'Click "Save Changes" to apply'
        ]
      }
    ]
  }
];

const rolePermissions = [
  { role: 'Super Admin', access: 'Full access to all features and settings' },
  { role: 'Admin', access: 'Full access except App Settings' },
  { role: 'Property Manager', access: 'Manage assigned property and its stores' },
  { role: 'Store Manager', access: 'Manage assigned store operations' },
  { role: 'Staff', access: 'Basic stock entry and viewing' }
];

export const HelpPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  const filteredCategories = guideCategories.filter(category => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      category.title.toLowerCase().includes(query) ||
      category.guides.some(guide => 
        guide.title.toLowerCase().includes(query) ||
        guide.steps?.some(step => step.toLowerCase().includes(query))
      )
    );
  });

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
          <BookOpen className="h-5 w-5 text-primary" />
          <span className="font-semibold text-primary">Help Guide</span>
        </div>
        <h1 className="text-3xl font-bold text-foreground mb-2">How to Use the System</h1>
        <p className="text-muted-foreground">
          Quick reference guide for all features. Click on any section to learn more.
        </p>
      </div>

      {/* Search */}
      <div className="max-w-md mx-auto">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search guides..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12"
            data-testid="help-search"
          />
        </div>
      </div>

      {/* Quick Tips Card */}
      <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-amber-100">
              <Lightbulb className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-lg mb-2">Quick Tips for Fast Operations</h3>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Use <strong>Quick Actions</strong> (Quick Issue, Quick Inward, Quick Transfer) for daily operations
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <Star className="h-4 w-4 text-yellow-500" /> Star your frequently used items for quick access
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Recent items and last used store are remembered automatically
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  Check the Dashboard daily for low stock alerts
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Grid */}
      {!selectedCategory ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCategories.map((category) => {
            const Icon = category.icon;
            return (
              <Card 
                key={category.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setSelectedCategory(category)}
                data-testid={`help-category-${category.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className={cn("p-3 rounded-xl", category.color)}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-1">{category.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        {category.guides.length} guide{category.guides.length > 1 ? 's' : ''}
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* Selected Category Detail View */
        <div className="space-y-6">
          <Button 
            variant="ghost" 
            onClick={() => setSelectedCategory(null)}
            className="mb-4"
          >
            ← Back to all guides
          </Button>
          
          <div className="flex items-center gap-4 mb-6">
            <div className={cn("p-4 rounded-xl", selectedCategory.color)}>
              <selectedCategory.icon className="h-8 w-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{selectedCategory.title}</h2>
              <p className="text-muted-foreground">{selectedCategory.guides.length} guides available</p>
            </div>
          </div>

          <div className="space-y-6">
            {selectedCategory.guides.map((guide, index) => (
              <Card key={index} data-testid={`guide-${index}`}>
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl">{guide.title}</CardTitle>
                  {guide.subtitle && (
                    <CardDescription>{guide.subtitle}</CardDescription>
                  )}
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Steps */}
                  <div className="space-y-3">
                    {guide.steps.map((step, stepIndex) => (
                      <div key={stepIndex} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-sm font-semibold text-primary">{stepIndex + 1}</span>
                        </div>
                        <p className="text-sm leading-relaxed pt-1">{step}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Tips if available */}
                  {guide.tips && guide.tips.length > 0 && (
                    <div className="mt-4 p-4 rounded-lg bg-blue-50 border border-blue-100">
                      <p className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-2">
                        <Lightbulb className="h-4 w-4" /> Pro Tips
                      </p>
                      <ul className="space-y-1">
                        {guide.tips.map((tip, tipIndex) => (
                          <li key={tipIndex} className="text-sm text-blue-700 flex items-start gap-2">
                            <span className="text-blue-400 mt-1">•</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Role Permissions Quick Reference */}
      {!selectedCategory && (
        <Card data-testid="role-permissions-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              Role Permissions Quick Reference
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {rolePermissions.map((item, index) => (
                <div key={index} className="flex items-center gap-4 p-3 rounded-lg bg-muted/50">
                  <div className="w-32 flex-shrink-0">
                    <span className="font-semibold text-sm">{item.role}</span>
                  </div>
                  <span className="text-sm text-muted-foreground">{item.access}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Common Workflows */}
      {!selectedCategory && (
        <Card data-testid="common-workflows-card">
          <CardHeader>
            <CardTitle>Common Daily Workflows</CardTitle>
            <CardDescription>Most used operations by department</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                  Kitchen Team
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1 ml-5">
                  <li>• Use Quick Issue to log ingredient usage</li>
                  <li>• Check Kitchen Store stock on Dashboard</li>
                  <li>• Request transfers from Main Store when low</li>
                </ul>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                  Housekeeping Team
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1 ml-5">
                  <li>• Use Quick Issue for daily supplies</li>
                  <li>• Track room amenities usage</li>
                  <li>• Report low stock items to manager</li>
                </ul>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  Store Manager
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1 ml-5">
                  <li>• Use Quick Inward when receiving deliveries</li>
                  <li>• Process transfer requests from departments</li>
                  <li>• Monitor low stock alerts daily</li>
                  <li>• Generate reports for management</li>
                </ul>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                  Maintenance Team
                </h4>
                <ul className="text-sm text-muted-foreground space-y-1 ml-5">
                  <li>• Use Quick Issue for repair materials</li>
                  <li>• Track maintenance supplies usage</li>
                  <li>• Star frequently used items (bulbs, pipes, etc.)</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Need Help */}
      {!selectedCategory && (
        <div className="text-center py-8">
          <HelpCircle className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
          <h3 className="font-semibold mb-2">Need More Help?</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            Contact your system administrator for additional support or to request new features.
          </p>
        </div>
      )}
    </div>
  );
};

export default HelpPage;
